import React, { useState, useEffect } from 'react';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
} from '@mui/material';
import { FieldMapping } from '../../services/fieldMapping.service';
import { isFieldHidden, isFieldRequired } from '../../utils/formUtils';

interface TherapyInformationProps {
  data?: any;
  onChange?: (data: any) => void;
  formData?: any;
  onFormDataChange?: (data: any) => void;
  fieldMappings?: FieldMapping[];
  isRequired?: boolean;
  readOnly?: boolean;
}

interface FormData {
  // Hospitalization 1
  hospitalization1_facility: string;
  hospitalization1_city: string;
  hospitalization1_state: string;
  hospitalization1_admission_date: string;
  hospitalization1_discharge_date: string;
  hospitalization1_reason: string;

  // Hospitalization 2
  hospitalization2_facility: string;
  hospitalization2_city: string;
  hospitalization2_state: string;
  hospitalization2_admission_date: string;
  hospitalization2_discharge_date: string;
  hospitalization2_reason: string;

  // Hospitalization 3
  hospitalization3_facility: string;
  hospitalization3_city: string;
  hospitalization3_state: string;
  hospitalization3_admission_date: string;
  hospitalization3_discharge_date: string;
  hospitalization3_reason: string;

  // Hospitalization 4
  hospitalization4_facility: string;
  hospitalization4_city: string;
  hospitalization4_state: string;
  hospitalization4_admission_date: string;
  hospitalization4_discharge_date: string;
  hospitalization4_reason: string;

  // Therapy 1
  therapy1_facility_therapist: string;
  therapy1_type: string;
  therapy1_start_date: string;
  therapy1_end_date: string;
  therapy1_frequency: string;

  // Therapy 2
  therapy2_facility_therapist: string;
  therapy2_type: string;
  therapy2_start_date: string;
  therapy2_end_date: string;
  therapy2_frequency: string;

  // Therapy 3
  therapy3_facility_therapist: string;
  therapy3_type: string;
  therapy3_start_date: string;
  therapy3_end_date: string;
  therapy3_frequency: string;

  // Therapy 4
  therapy4_facility_therapist: string;
  therapy4_type: string;
  therapy4_start_date: string;
  therapy4_end_date: string;
  therapy4_frequency: string;
}

const therapyTypes = [
  'Chiropractic',
  'Physical',
  'Occupational',
  'Vestibular',
  'Aqua',
  'Massage',
  'Acupuncture',
];

const TherapyInformation: React.FC<TherapyInformationProps> = ({
  data,
  onChange,
  formData: initialFormData,
  onFormDataChange,
  fieldMappings = [],
  readOnly = false,
}) => {
  const actualFormData = initialFormData || data || {};
  const actualOnChange =
    onFormDataChange ||
    onChange ||
    ((_data: any) => {
      /* noop */
    });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isFieldHiddenLocal  = (f: string) => isFieldHidden(f, 'therapy_information', fieldMappings);
  const isFieldRequiredLocal = (f: string) => isFieldRequired(f, 'therapy_information', fieldMappings);
  const [formData, setFormData] = useState<FormData>({
    // Hospitalization 1
    hospitalization1_facility: '',
    hospitalization1_city: '',
    hospitalization1_state: '',
    hospitalization1_admission_date: '',
    hospitalization1_discharge_date: '',
    hospitalization1_reason: '',

    // Hospitalization 2
    hospitalization2_facility: '',
    hospitalization2_city: '',
    hospitalization2_state: '',
    hospitalization2_admission_date: '',
    hospitalization2_discharge_date: '',
    hospitalization2_reason: '',

    // Hospitalization 3
    hospitalization3_facility: '',
    hospitalization3_city: '',
    hospitalization3_state: '',
    hospitalization3_admission_date: '',
    hospitalization3_discharge_date: '',
    hospitalization3_reason: '',

    // Hospitalization 4
    hospitalization4_facility: '',
    hospitalization4_city: '',
    hospitalization4_state: '',
    hospitalization4_admission_date: '',
    hospitalization4_discharge_date: '',
    hospitalization4_reason: '',

    // Therapy 1
    therapy1_facility_therapist: '',
    therapy1_type: '',
    therapy1_start_date: '',
    therapy1_end_date: '',
    therapy1_frequency: '',

    // Therapy 2
    therapy2_facility_therapist: '',
    therapy2_type: '',
    therapy2_start_date: '',
    therapy2_end_date: '',
    therapy2_frequency: '',

    // Therapy 3
    therapy3_facility_therapist: '',
    therapy3_type: '',
    therapy3_start_date: '',
    therapy3_end_date: '',
    therapy3_frequency: '',

    // Therapy 4
    therapy4_facility_therapist: '',
    therapy4_type: '',
    therapy4_start_date: '',
    therapy4_end_date: '',
    therapy4_frequency: '',
    ...actualFormData,
  });

  useEffect(() => {
    setFormData((prev: FormData) => ({ ...prev, ...actualFormData }));
  }, [actualFormData]);

  const handleChange = (field: string, value: any) => {
    if (readOnly) return; // Prevent changes in read-only mode
    const updatedData = { ...formData, [field]: value };
    setFormData(updatedData);
    actualOnChange(updatedData);
  };

  const renderHospitalizationFields = (hospitalizationNumber: number) => (
    <Box
      sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1, p: 2, mb: 2 }}
    >
      <Typography variant='subtitle1' gutterBottom>
        Hospitalization {hospitalizationNumber}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label='Facility Name'
          value={
            formData[
              `hospitalization${hospitalizationNumber}_facility` as keyof FormData
            ]
          }
          onChange={e =>
            handleChange(
              `hospitalization${hospitalizationNumber}_facility`,
              e.target.value
            )
          }
          required={isFieldRequiredLocal(`hospitalization${hospitalizationNumber}_facility`)}
          fullWidth
        />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label='City'
            value={
              formData[
                `hospitalization${hospitalizationNumber}_city` as keyof FormData
              ]
            }
            onChange={e =>
              handleChange(
                `hospitalization${hospitalizationNumber}_city`,
                e.target.value
              )
            }
            sx={{ flex: 1 }}
          />
          <TextField
            label='State'
            value={
              formData[
                `hospitalization${hospitalizationNumber}_state` as keyof FormData
              ]
            }
            onChange={e =>
              handleChange(
                `hospitalization${hospitalizationNumber}_state`,
                e.target.value
              )
            }
            sx={{ flex: 1 }}
          />
        </Box>
        <TextField
          label='Admission Date'
          type='date'
          value={
            formData[
              `hospitalization${hospitalizationNumber}_admission_date` as keyof FormData
            ]
          }
          onChange={e =>
            handleChange(
              `hospitalization${hospitalizationNumber}_admission_date`,
              e.target.value
            )
          }
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
        <TextField
          label='Discharge Date'
          type='date'
          value={
            formData[
              `hospitalization${hospitalizationNumber}_discharge_date` as keyof FormData
            ]
          }
          onChange={e =>
            handleChange(
              `hospitalization${hospitalizationNumber}_discharge_date`,
              e.target.value
            )
          }
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
        <TextField
          label='Reason for Hospitalization'
          multiline
          rows={2}
          value={
            formData[
              `hospitalization${hospitalizationNumber}_reason` as keyof FormData
            ]
          }
          onChange={e =>
            handleChange(
              `hospitalization${hospitalizationNumber}_reason`,
              e.target.value
            )
          }
          fullWidth
        />
      </Box>
    </Box>
  );

  const renderTherapyFields = (therapyNumber: number) => (
    <Box
      sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1, p: 2, mb: 2 }}
    >
      <Typography variant='subtitle1' gutterBottom>
        Therapy {therapyNumber}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label='Facility & Therapist Name'
          value={
            formData[
              `therapy${therapyNumber}_facility_therapist` as keyof FormData
            ]
          }
          onChange={e =>
            handleChange(
              `therapy${therapyNumber}_facility_therapist`,
              e.target.value
            )
          }
          fullWidth
        />
        <FormControl fullWidth>
          <InputLabel>Type of Therapy</InputLabel>
          <Select
            value={formData[`therapy${therapyNumber}_type` as keyof FormData]}
            label='Type of Therapy'
            onChange={e =>
              handleChange(`therapy${therapyNumber}_type`, e.target.value)
            }
          >
            {therapyTypes.map(type => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label='Start Date'
          type='date'
          value={
            formData[`therapy${therapyNumber}_start_date` as keyof FormData]
          }
          onChange={e =>
            handleChange(`therapy${therapyNumber}_start_date`, e.target.value)
          }
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
        <TextField
          label='End Date'
          type='date'
          value={formData[`therapy${therapyNumber}_end_date` as keyof FormData]}
          onChange={e =>
            handleChange(`therapy${therapyNumber}_end_date`, e.target.value)
          }
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
        <TextField
          label='Frequency of Visit'
          value={
            formData[`therapy${therapyNumber}_frequency` as keyof FormData]
          }
          onChange={e =>
            handleChange(`therapy${therapyNumber}_frequency`, e.target.value)
          }
          fullWidth
        />
      </Box>
    </Box>
  );

  return (
    <Box>
      {/* Hospitalization Information Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant='h5' gutterBottom>
          Hospitalization Information
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {renderHospitalizationFields(1)}
          {renderHospitalizationFields(2)}
          {renderHospitalizationFields(3)}
          {renderHospitalizationFields(4)}
        </Box>
      </Box>

      {/* Therapy Information Section */}
      <Box>
        <Typography variant='h5' gutterBottom>
          Therapy Information
        </Typography>
        <Typography variant='subtitle2' color='text.secondary' sx={{ mb: 3 }}>
          (Chiropractic, Physical, Occupational, Vestibular, Aqua, Massage,
          Acupuncture)
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {renderTherapyFields(1)}
          {renderTherapyFields(2)}
          {renderTherapyFields(3)}
          {renderTherapyFields(4)}
        </Box>
      </Box>
    </Box>
  );
};

export default TherapyInformation;
